import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { ChevronLeft, ShoppingCart } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      if (r.data.sizes?.length) setSize(r.data.sizes[0]);
    }).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) return <div className="p-20 text-center text-[#A1A1AA]">Loading…</div>;

  const handleAdd = () => {
    if (product.sizes?.length && !size) { toast.error("Please select a size"); return; }
    addItem(product, size, qty);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-[#A1A1AA] hover:text-white transition-colors mb-8">
        <ChevronLeft size={16} /> Back to shop
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="bg-[#1A1A1A] border border-white/10 overflow-hidden">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-detail-image" />
        </div>
        <div>
          <span className="text-[#C5A059] text-xs font-bold uppercase tracking-[0.3em]">{product.category}</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-900 uppercase tracking-tight mt-3 leading-none" data-testid="product-detail-name">
            {product.name}
          </h1>
          <p className="text-3xl font-heading font-700 text-[#E63946] mt-4" data-testid="product-detail-price">
            ${product.price.toFixed(2)}
          </p>
          <p className="text-[#A1A1AA] leading-relaxed mt-6">{product.description}</p>

          {product.sizes?.length > 0 && (
            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-3">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`w-12 h-12 border text-sm font-semibold transition-colors ${
                      size === s ? "border-[#E63946] bg-[#E63946] text-white" : "border-white/20 text-white hover:border-white/50"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-white/20">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 text-white hover:bg-white/10 transition-colors" data-testid="qty-minus">−</button>
              <span className="w-12 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-12 h-12 text-white hover:bg-white/10 transition-colors" data-testid="qty-plus">+</button>
            </div>
          </div>

          <button onClick={handleAdd} data-testid="add-to-cart-btn"
            className="mt-8 w-full sm:w-auto bg-[#E63946] hover:bg-[#FF4D5A] text-white font-semibold uppercase tracking-wider text-sm px-10 py-4 flex items-center justify-center gap-2 transition-colors">
            <ShoppingCart size={18} /> Add To Cart
          </button>
        </div>
      </div>
    </div>
  );
}
